import React, { useMemo, useState, useEffect, useRef } from "react";
import { Loader } from "@transfr-inc/dashboard-components";
import container from "@src/container";
import { useApiRequest } from "@lib/hooks";
import "./classroom.progress.prog.tab.scss";
import {
  getGroupIds,
  mapCredentialData,
} from "@views/my-work/components/course/course.utils";
import { AwardsProgressTracker } from "@components/progress-tracker";
import { ClassroomProgressLeaderboardTable } from "@views/classrooms/details/components/classroom.prog.leaderboard.table";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { ExportAwardsModal } from "@components/modals/export-awards/export-awards.modal";

export function ClassroomProgressProgTab({
  classroom,
  users,
  responsiveStyles,
  courseData,
  activityStatus,
  courseDataLoading,
}) {
  const [exportAwardsModalOpen, setExportAwardsModalOpen] = useState(false);
  const [mappedClassromStats, setMappedClassromStats] = useState(null);
  const lastProcessedActivityStatusRef = useRef(null);
  const lastBaseStatsRef = useRef(null);
  const { courseService, insightsService } = container;
  const { courseId, classroomId } = classroom;

  const {
    response: classroomAwardsLinks,
    loading: classroomAwardsLinksLoading,
  } = useApiRequest(() => courseService.getAwardsLinksByClassroom(classroomId));

  const { response: courseCredentials, loading: courseCredentialsLoading, sendRequest: getCourseCredentials } =
    useApiRequest(() => courseService.getCourseCredentials(courseId), false);

  const {
    response: classroomTraineesCompletionSummaryStats,
    loading: classroomTraineesCompletionSummaryStatsLoading,
  } = useApiRequest(() =>
    insightsService.getProgClassroomTraineesCompletionSummaryStats(
      classroomId,
      courseId
    )
  );

  useEffect(() => {
    if (!classroomTraineesCompletionSummaryStats?.completed_stats) {
      setMappedClassromStats(null);
      lastProcessedActivityStatusRef.current = null;
      lastBaseStatsRef.current = null;
      return;
    }

    // Always use base stats as the foundation
    const baseStats = classroomTraineesCompletionSummaryStats;

    // Process activityStatus if it's new and indicates a completion
    if (
      activityStatus &&
      activityStatus.user_id + activityStatus.activity_id !==
        lastProcessedActivityStatusRef.current
    ) {
      try {
        const parsedActivityStatus = activityStatus;
        if (parsedActivityStatus?.complete && parsedActivityStatus?.user_id) {
          // Use current mapped stats (which may already have increments) or base stats
          const currentStats = mappedClassromStats || baseStats;
          const currentCompletedStats = currentStats.completed_stats || {};
          const newCount =
            (currentCompletedStats[parsedActivityStatus.user_id] || 0) + 1;

          setMappedClassromStats({
            ...currentStats,
            completed_stats: {
              ...currentCompletedStats,
              [parsedActivityStatus.user_id]: newCount,
            },
          });
          lastProcessedActivityStatusRef.current =
            activityStatus.user_id + activityStatus.activity_id;
        }
      } catch (error) {
        console.error("Failed to parse activityStatus:", error);
      }
    } else {
      setMappedClassromStats(mappedClassromStats || baseStats);
    }
  }, [activityStatus, classroomTraineesCompletionSummaryStats]);

  const allGroupIds = useMemo(() => {
    if (!courseCredentials || !courseData) return [];

    const { mainCredential, courseInfo } = mapCredentialData(
      courseCredentials,
      courseData
    );
    return getGroupIds(mainCredential, courseInfo);
  }, [courseCredentials, courseData]);

  const {
    sendRequest: getCourseCredentialsSummaryStats,
    response: courseCredentialsSummaryStats,
    loading: courseCredentialsSummaryStatsLoading,
  } = useApiRequest(
    () =>
      insightsService.getCourseCredentialsSummaryStats(
        classroomId,
        allGroupIds
      ),
    false
  );

  useEffect(() => {
    if (allGroupIds.length > 0) {
      getCourseCredentialsSummaryStats();
    }
  }, [allGroupIds.length]);

  useEffect(() => {
    if (courseId) {
      getCourseCredentials();
    }
  }, [courseId]);

  const { mainCredential, courseInfo } = useMemo(() => {
    if (!courseCredentials || !courseData) {
      return { mainCredential: null, courseInfo: [] };
    }

    const { mainCredential: rawMain, courseInfo: rawCourseInfo } =
      mapCredentialData(courseCredentials, courseData);

    if (!courseCredentialsSummaryStats) {
      return { mainCredential: rawMain, courseInfo: rawCourseInfo };
    }

    const decoratedCourseInfo = rawCourseInfo.map((info) => ({
      ...info,
      totalAwarded: courseCredentialsSummaryStats[String(info?.groupId)] || 0,
    }));

    const decoratedMainCredential = rawMain
      ? {
          ...rawMain,
          totalAwarded:
            courseCredentialsSummaryStats[String(rawMain.groupId)] || 0,
        }
      : null;

    return {
      mainCredential: decoratedMainCredential,
      courseInfo: decoratedCourseInfo,
    };
  }, [courseCredentials, courseData, courseCredentialsSummaryStats]);

  const shouldRenderAwards = useMemo(() => {
    return mainCredential || courseInfo?.length > 0;
  }, [mainCredential, courseInfo]);

  const shouldRenderLeaderboard = useMemo(() => {
    return (
      users?.length > 0 &&
      mappedClassromStats?.total_activities &&
      mappedClassromStats?.completed_stats &&
      mappedClassromStats?.exempt_stats
    );
  }, [users, mappedClassromStats]);

  const isLoading = useMemo(() => {
    return (
      courseDataLoading ||
      courseCredentialsLoading ||
      courseCredentialsSummaryStatsLoading ||
      classroomTraineesCompletionSummaryStatsLoading ||
      classroomAwardsLinksLoading
    );
  }, [
    courseDataLoading,
    courseCredentialsLoading,
    courseCredentialsSummaryStatsLoading,
    classroomTraineesCompletionSummaryStatsLoading,
    classroomAwardsLinksLoading,
  ]);

  const usersWithAwards = useMemo(() => {
    if (!shouldRenderAwards || !classroomAwardsLinks) return [];

    const credentialInfoMap = new Map(
      [...courseInfo, mainCredential].filter(Boolean).map((cred) => [
        cred.groupId,
        {
          type: cred.type,
          title: cred.title,
          description: cred.description,
        },
      ])
    );

    return users
      .filter((user) => classroomAwardsLinks[user.userId])
      .map((user) => {
        const awardsLinksForUser = classroomAwardsLinks[user.userId];
        const awards = Object.entries(awardsLinksForUser)
          .map(([groupId, link]) => {
            const credentialInfo = credentialInfoMap.get(Number(groupId));
            return credentialInfo
              ? { groupId: Number(groupId), link, ...credentialInfo }
              : null;
          })
          .filter(Boolean)
          .sort((a, b) => {
            const typeOrder = { MAIN: 0, MICRO: 1 };
            const typeA = typeOrder[a.type] ?? 2;
            const typeB = typeOrder[b.type] ?? 2;

            return typeA - typeB;
          });

        return { ...user, awards };
      })
      .sort((a, b) => {
        const lastNameA = a.lastName.toUpperCase();
        const lastNameB = b.lastName.toUpperCase();
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        return 0;
      });
  }, [
    users,
    classroomAwardsLinks,
    shouldRenderAwards,
    courseInfo,
    mainCredential,
  ]);

  return (
    <>
      {isLoading && <Loader overlay fixed />}
      {shouldRenderAwards && !isLoading && (
        <div className="course-progress-awards-container">
          <div className="course-progress-awards-header-container">
            <div className="course-progress-section-title">Awards</div>
            <div className="course-progress-buttons-container">
              <Button
                primary
                size="small"
                icon={["fa-light", "file-export"]}
                onClick={() => setExportAwardsModalOpen(true)}
              >
                Export
              </Button>
            </div>
          </div>
          <AwardsProgressTracker
            mainCredential={mainCredential}
            courseInfo={courseInfo}
          />
        </div>
      )}
      {shouldRenderLeaderboard && !isLoading && (
        <div className="course-progress-leaderboard-container">
          <div className="course-progress-leaderboard-title">
            Active Learners
            {users.length > 0 && <span>: {users.length}</span>}
          </div>
          <ClassroomProgressLeaderboardTable
            classroom={classroom}
            data={mappedClassromStats}
            users={users}
            responsiveStyles={responsiveStyles}
          />
        </div>
      )}
      <ExportAwardsModal
        open={exportAwardsModalOpen}
        onClose={() => setExportAwardsModalOpen(false)}
        awardsLinksMap={classroomAwardsLinks}
        classroom={classroom}
        users={usersWithAwards}
        mainCredential={mainCredential}
        otherAwards={courseInfo}
      />
    </>
  );
}
